import {Alert, Loader, Select, Stack, Title} from '@mantine/core'
import {useForm, type UseFormReturnType} from '@mantine/form'
import {IconUser} from '@tabler/icons-react'
import type {ReactNode} from 'react'

import {useCurrentUser} from '../auth/use-current-user'
import type {CreateProjectFormValues} from '../projects/create-project-form-values'
import {toWizardFormValues} from '../projects/project-billing-terms-utils'
import type {ProjectBillingMethod} from '../projects/schemas'
import {repOptions} from '../users/rep-options'
import {useProjectContacts} from '../users/use-project-contacts'
import {StepBillingTerms} from './step-billing-terms'
import type {CreateJobFormValues} from './types'

type NewWorkFieldsProps = {
    form: UseFormReturnType<CreateJobFormValues>
    onBillingMethodChange: (method: ProjectBillingMethod) => void
    methodNote?: ReactNode
}

export function NewWorkFields({form, onBillingMethodChange, methodNote}: NewWorkFieldsProps) {
    const {data: contacts = [], isLoading, isError, error} = useProjectContacts()
    const currentUserId = useCurrentUser()?.id ?? null

    // StepBillingTerms takes the project form shape
    const termsForm = useForm<CreateProjectFormValues>({
        initialValues: toWizardFormValues({
            billingMethod: form.values.billingMethod,
            billingTerms: form.values.billingTerms,
        }),
        onValuesChange: (values, previous) => {
            if (values.billingMethod !== previous.billingMethod) {
                onBillingMethodChange(values.billingMethod)
            }
            form.setValues({billingMethod: values.billingMethod, billingTerms: values.billingTerms})
        },
    })
    const termsErrors = Object.fromEntries(
        Object.entries(form.errors).filter(([key]) => key.startsWith('billingTerms.')),
    )

    return (
        <Stack gap="md">
            {isLoading ? (
                <Loader size="sm" />
            ) : isError ? (
                <Alert color="red" title="Failed to load reps">
                    {error instanceof Error ? error.message : 'Something went wrong'}
                </Alert>
            ) : (
                <Select
                    label="Rep"
                    description="Internal contact for this order"
                    leftSection={<IconUser size={16} />}
                    data={repOptions(contacts, {
                        currentUserId,
                        value: form.values.internalContactId,
                    })}
                    searchable
                    withAsterisk
                    {...form.getInputProps('internalContactId')}
                />
            )}
            <Title order={5}>Billing terms</Title>
            <StepBillingTerms
                form={{...termsForm, errors: termsErrors}}
                showManualCharges={false}
                methodNote={methodNote}
            />
        </Stack>
    )
}
